import { HEAD_META_NAME_TAGS, HEAD_META_PROPERTY_TAGS } from "@/main/navigation/HeadMetaManifest";
import { setBaseDocumentTitle } from "@/main/navigation/DocumentTitleBadge";

// HeadSync copies the full declared set of synchronized <head> meta tags out of the fetched server
// document on a document-swap soft navigation. The set lives in HeadMetaManifest so it cannot drift
// from the thin-shell (client-route) sync path that synthesizes the same SEO/share surface by hand.

function replaceOrRemoveUniqueNode(selector: string, nextNode: Element | null): void {
    const currentNode = document.head.querySelector(selector);

    if (!nextNode) {
        currentNode?.remove();
        return;
    }

    const clonedNode = nextNode.cloneNode(true) as Element;
    if (currentNode) {
        currentNode.replaceWith(clonedNode);
        return;
    }

    document.head.appendChild(clonedNode);
}

export function syncHeadFromDocument(nextDocument: Document): void {
    // Hands the title to DocumentTitleBadge rather than assigning it. This function overwrites the
    // title wholesale on every document-swap navigation, so any unread prefix written elsewhere would
    // be silently erased here -- and a prefix re-applied by an observer racing this line would be a
    // second writer of one value. One owner instead.
    setBaseDocumentTitle(nextDocument.title);

    const nextLang = nextDocument.documentElement.lang?.trim();
    if (nextLang) {
        document.documentElement.lang = nextLang;
    }

    replaceOrRemoveUniqueNode(
        'link[rel="canonical"]',
        nextDocument.head.querySelector('link[rel="canonical"]'),
    );

    HEAD_META_NAME_TAGS.forEach(({ key }) => {
        replaceOrRemoveUniqueNode(
            `meta[name="${key}"]`,
            nextDocument.head.querySelector(`meta[name="${key}"]`),
        );
    });

    HEAD_META_PROPERTY_TAGS.forEach(({ key }) => {
        replaceOrRemoveUniqueNode(
            `meta[property="${key}"]`,
            nextDocument.head.querySelector(`meta[property="${key}"]`),
        );
    });
}
